import type { AttachmentRef } from '@iq/contracts/v1/upload';

/**
 * Extract upload refs from the LAST user message's `parts` array.
 *
 * `chat-runtime-provider.tsx` hands this the messages payload from
 * assistant-ui's `prepareSendMessagesRequest` callback. The runtime
 * serializes each composer attachment's `content` (built by our
 * `upload-adapter.send()`) through `toCreateMessage` (upstream
 * `@assistant-ui/react-ai-sdk`), which turns
 * `{type:'file', data: <upload_id>, mimeType, filename}` into the AI SDK
 * UI-message wire shape
 * `{type:'file', url: <upload_id>, mediaType, filename}`. We pull the
 * `url` (the upload_id we stashed in `send()`) back out and project the
 * top-level `body.attachments` array the server contract expects.
 *
 * Server-side every active upload bound to the active thread is ALSO
 * auto-included from the DB (see `chat-handler.ts` "attachment-resolution
 * stage", Commit F). The client-sent refs are unioned with the
 * thread-sticky set + de-duplicated by `uploadId`; per-user ownership is
 * re-checked on every ref BEFORE retrieval is hit. The client-sent list
 * therefore expresses "this turn's intent" — turns the user wants to
 * include — without re-asserting authority over uploads bound to OTHER
 * threads.
 */
export function extractAttachmentsFromMessages(
  messages: ReadonlyArray<{ role?: string; parts?: ReadonlyArray<unknown> }>,
): AttachmentRef[] {
  if (messages.length === 0) return [];
  let lastUser: { parts?: ReadonlyArray<unknown> } | undefined;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg?.role === 'user') {
      lastUser = msg;
      break;
    }
  }
  if (!lastUser?.parts) return [];

  const refs: AttachmentRef[] = [];
  for (const rawPart of lastUser.parts) {
    if (!rawPart || typeof rawPart !== 'object') continue;
    const part = rawPart as {
      type?: unknown;
      url?: unknown;
      mediaType?: unknown;
      filename?: unknown;
    };
    if (part.type !== 'file') continue;
    if (typeof part.url !== 'string' || part.url.length === 0) continue;
    if (typeof part.mediaType !== 'string' || part.mediaType.length === 0) continue;
    const filename =
      typeof part.filename === 'string' && part.filename.length > 0 ? part.filename : 'file';
    refs.push({
      uploadId: part.url,
      mediaType: part.mediaType as AttachmentRef['mediaType'],
      filename,
    });
  }
  return refs;
}
